import { notFound } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { prisma } from "#/db";
import type { Training } from "#/generated/prisma/client";
import { PARCOURS_SLUG } from "#/lib/entrepreneur-101";
import { LOCALES } from "#/lib/i18n";

// Catches malformed content (e.g. pasted in the Trainings admin JSON textarea) early, with a clear error.
function assertValidContent(training: Training) {
	const content = training.content as Record<string, unknown> | null;
	const missing = LOCALES.filter(
		(locale) =>
			!content ||
			typeof content[locale] !== "object" ||
			content[locale] === null,
	);
	if (missing.length) {
		throw new Error(
			`Training "${training.slug}" is missing content for locale(s): ${missing.join(", ")}`,
		);
	}
}

export const getTrainingBySlug = createServerFn({ method: "GET" })
	.validator((slug: string) => slug)
	.handler(async ({ data: slug }) => {
		const training = await prisma.training.findUnique({ where: { slug } });
		if (!training || !training.isActive) {
			throw notFound();
		}
		assertValidContent(training);
		return training;
	});

export const getDefaultTraining = createServerFn({ method: "GET" }).handler(
	async () => {
		const training = await prisma.training.findFirst({
			where: { isDefault: true, isActive: true },
		});
		if (!training) {
			throw notFound();
		}
		assertValidContent(training);
		return training;
	},
);

/**
 * Where "/" sends a visitor: the training currently being sold. In order —
 * the default training if it is live; otherwise the live training whose next
 * (or running) edition comes soonest; otherwise the live training edited most
 * recently. Null only when no training is live at all. The Entrepreneur 101
 * path row is never a candidate: its page is the (hidden) home page itself.
 */
export const getActiveTrainingSlug = createServerFn({ method: "GET" }).handler(
	async () => {
		const live = { isActive: true, slug: { not: PARCOURS_SLUG } };

		const preferred = await prisma.training.findFirst({
			where: { ...live, isDefault: true },
			select: { slug: true },
		});
		if (preferred) return preferred.slug;

		// Editions are stored at UTC midnight, so "not over yet" is an end date
		// from the start of today (UTC) onwards.
		const today = new Date();
		today.setUTCHours(0, 0, 0, 0);
		const upcoming = await prisma.trainingEdition.findFirst({
			where: { endDate: { gte: today }, training: live },
			orderBy: [{ startDate: "asc" }, { courseNumber: "asc" }],
			select: { training: { select: { slug: true } } },
		});
		if (upcoming) return upcoming.training.slug;

		const latest = await prisma.training.findFirst({
			where: live,
			orderBy: { updatedAt: "desc" },
			select: { slug: true },
		});
		return latest?.slug ?? null;
	},
);
